import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Users, MessageCircle, TrendingUp, Target, ArrowRight } from 'lucide-react';
interface AccountabilityPodsPageProps {
  onNavigate?: (path: string) => void;
}
export function AccountabilityPodsPage({
  onNavigate
}: AccountabilityPodsPageProps) {
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933]" data-id="element-104">
      <Navbar onNavigate={onNavigate} data-id="element-105" />

      <section className="py-24" data-id="element-106">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-107">
          <div className="text-center mb-16" data-id="element-108">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-109">
              Accountability Pods
            </h1>
            <p className="text-lg font-mono text-[#9ca3af] max-w-2xl mx-auto" data-id="element-110">
              Small groups of peers who keep you consistent through structured
              pressure and shared momentum
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16" data-id="element-111">
            <div data-id="element-112">
              <h2 className="text-2xl font-bold mb-6" data-id="element-113">How Pods Work</h2>
              <div className="space-y-6" data-id="element-114">
                <div className="flex items-start gap-4" data-id="element-115">
                  <Users className="w-6 h-6 text-salon-red flex-shrink-0 mt-1" data-id="element-116" />
                  <div data-id="element-117">
                    <h3 className="font-bold mb-2" data-id="element-118">Small Group Formation</h3>
                    <p className="text-sm text-[#1f2933]/80" data-id="element-119">
                      Groups of 4-6 professionals matched by industry, goals,
                      and commitment level
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-120">
                  <MessageCircle className="w-6 h-6 text-salon-teal flex-shrink-0 mt-1" data-id="element-121" />
                  <div data-id="element-122">
                    <h3 className="font-bold mb-2" data-id="element-123">Weekly Check-ins</h3>
                    <p className="text-sm text-[#1f2933]/80" data-id="element-124">
                      Structured sessions where you share progress, challenges,
                      and get peer feedback
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-125">
                  <Target className="w-6 h-6 text-salon-yellow flex-shrink-0 mt-1" data-id="element-126" />
                  <div data-id="element-127">
                    <h3 className="font-bold mb-2" data-id="element-128">Goal Setting & Tracking</h3>
                    <p className="text-sm text-[#1f2933]/80" data-id="element-129">
                      Set weekly content goals and hold each other accountable
                      to hitting them
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-130">
                  <TrendingUp className="w-6 h-6 text-salon-red flex-shrink-0 mt-1" data-id="element-131" />
                  <div data-id="element-132">
                    <h3 className="font-bold mb-2" data-id="element-133">Shared Momentum</h3>
                    <p className="text-sm text-[#1f2933]/80" data-id="element-134">
                      Celebrate wins together and push through challenges as a
                      unit
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-dashed border-[#9ca3af] p-8" data-id="element-135">
              <h3 className="text-xl font-bold mb-6" data-id="element-136">What You Get</h3>
              <ul className="space-y-4" data-id="element-137">
                <li className="flex items-start gap-3" data-id="element-138">
                  <span className="text-salon-red font-bold" data-id="element-139">→</span>
                  <span className="text-sm" data-id="element-140">
                    Matched with peers at similar stages
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-141">
                  <span className="text-salon-red font-bold" data-id="element-142">→</span>
                  <span className="text-sm" data-id="element-143">
                    Weekly accountability check-ins
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-144">
                  <span className="text-salon-red font-bold" data-id="element-145">→</span>
                  <span className="text-sm" data-id="element-146">Peer review on your content</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-147">
                  <span className="text-salon-red font-bold" data-id="element-148">→</span>
                  <span className="text-sm" data-id="element-149">
                    Opportunity sharing and collaboration
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-150">
                  <span className="text-salon-red font-bold" data-id="element-151">→</span>
                  <span className="text-sm" data-id="element-152">
                    Private pod communication channel
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-153">
                  <span className="text-salon-red font-bold" data-id="element-154">→</span>
                  <span className="text-sm" data-id="element-155">
                    Structured pressure without chaos
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-[#1f2933] text-white p-12 text-center" data-id="element-156">
            <h2 className="text-2xl font-bold mb-4" data-id="element-157">
              Why Accountability Works
            </h2>
            <p className="max-w-2xl mx-auto mb-8 text-[#f5f5f7]/80" data-id="element-158">
              Studies show you're 65% more likely to achieve a goal when you
              commit to someone else. With regular check-ins, that number jumps
              to 95%. Pods turn isolated effort into shared momentum.
            </p>
            <Button size="large" onClick={() => onNavigate && onNavigate('/initiate-access')} className="bg-salon-red hover:bg-salon-red/90 border-salon-red" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-160" />} data-id="element-159">
              JOIN_A_POD
            </Button>
          </div>
        </div>
      </section>

      <Footer onNavigate={onNavigate} data-id="element-161" />
    </div>;
}